import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import * as React from 'react';

import { adaptSearchCriteria } from 'stores/adapters/bex-api/common/search-criteria-adapter';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { evaluateAndLogExperiment } from 'stores/utils';
import { getDateRangeFromSearchCriteria } from 'stores/adapters';
import { getShoppingContextInput } from 'src/common/stores/adapters/bex-api/search/shopping-context-adapter';
import { MultiItemStore } from 'src/common/stores/multi-item-store';

import { PropertyDateRangeInput, PropertySearchCriteriaInput } from 'src/common/__generated__/api';
import { ShoppingContextInput } from '__generated__/api';

interface SharedUiPropertyDetailsComponentProps {
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
}

interface SharedUiPropertyDetailsWithInputsProps {
  hotelId?: string;
  dateRange?: PropertyDateRangeInput;
  shoppingContext?: ShoppingContextInput;
  searchCriteria?: PropertySearchCriteriaInput;
  showExperienceScoreReviews?: boolean;
}

export interface SharedUiWrapperProps extends Partial<RouteComponentProps> {
  inputs: {
    propertyId: string;
    dateRange: PropertyDateRangeInput;
    shoppingContext: ShoppingContextInput;
    searchCriteria: PropertySearchCriteriaInput;
  };
  className?: string;
}

/**
 * HOC that'll check if the hotelId is properly set, and return a memoized component that'll
 * only re-trigger its graphql query if the hotelId changes. It will inject the inputs props
 * so your wrapper can pass it down to its shared-ui component, all other props are simply drilled
 * down.
 * @param SharedUiWrapper {React.FC<SharedUiWrapperProps>}
 */
export const withSharedUiPropertyDetails = <F extends object>(SharedUiWrapper: React.FC<SharedUiWrapperProps & F>) => {
  const SharedUiPropertyDetailsComponent: React.FC<SharedUiPropertyDetailsComponentProps & F> = inject(
    'currentHotel',
    'multiItem',
    'experiment',
    'context'
  )(
    observer(({ currentHotel, multiItem, experiment, context, ...props }) => {
      const { hotelId, searchCriteria } = currentHotel || {};

      const multiItemContext = multiItem?.multiItemContext;
      const shoppingContext = getShoppingContextInput(multiItemContext);

      const dateRange = searchCriteria && getDateRangeFromSearchCriteria(searchCriteria);
      const showExperienceScoreReviews = evaluateAndLogExperiment('MPD_Traveler_GEX_Score_Web', context, experiment) === 3;

      // This is safe, because you're not actually rendering the component here
      // So we're golden even if the hotelId is null.
      const Component = React.useMemo(
        () => withSharedUiPropertyDetailsInputs(SharedUiWrapper as React.FC<SharedUiWrapperProps>),
        [hotelId, shoppingContext.multiItem?.id, shoppingContext.multiItem?.packageType]
      );

      if (!hotelId || !searchCriteria) {
        return null;
      }

      const propertySearchCriteria = context && adaptSearchCriteria(context, searchCriteria);

      return (
        <Component
          hotelId={String(hotelId)}
          dateRange={dateRange ? dateRange : undefined}
          shoppingContext={shoppingContext}
          searchCriteria={propertySearchCriteria}
          showExperienceScoreReviews={showExperienceScoreReviews}
          {...props}
        />
      );
    })
  );

  return SharedUiPropertyDetailsComponent;
};

/**
 * Function that builds the graphql input for property-details shared-ui
 * components. Very useful to make sure that all shared-ui components get
 * the same inputs, so queries aren't duplicated.
 * Not for external use, only use within {withSharedUiPropertyDetails}
 * or at your own risk.
 */
const withSharedUiPropertyDetailsInputs = (SharedUiWrapper: React.FC<SharedUiWrapperProps>) => {
  const SharedUiPropertyDetailsWithInputs: React.FC<SharedUiPropertyDetailsWithInputsProps> = ({
    hotelId,
    dateRange,
    shoppingContext,
    searchCriteria,
    showExperienceScoreReviews,
    ...props
  }) => {
    const inputs = React.useMemo(
      () => ({
        propertyId: hotelId || '',
        dateRange: dateRange as PropertyDateRangeInput,
        shoppingContext: shoppingContext as ShoppingContextInput,
        isDateless: false,
        searchCriteria: searchCriteria as PropertySearchCriteriaInput,
        showExperienceScoreReviews: showExperienceScoreReviews,
      }),
      [hotelId, shoppingContext?.multiItem?.id, shoppingContext?.multiItem?.packageType]
    );

    return <SharedUiWrapper inputs={inputs} {...props} />;
  };

  return SharedUiPropertyDetailsWithInputs;
};
